import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { SetURLSearchParams } from "react-router";
import { UserStatus } from "~/config/types";
import type { Option } from "~/molecules/ControlledCombobox";

const USER_MANAGEMENT_STATUS_FILTER_OPTIONS = [
  UserStatus.active,
  UserStatus.pending,
  UserStatus.deactivated,
] as const satisfies readonly UserStatus[];

interface UseUserManagementFiltersParams {
  searchParams: URLSearchParams;
  setSearchParams: SetURLSearchParams;
}

export interface UserManagementFiltersModel {
  selectedStatuses: UserStatus[];
  queryStatuses: UserStatus[];
  statusOptions: Option[];
  setStatusFilter: (values: string[]) => void;
  clearStatusFilter: () => void;
}

const getValidStatusFilterValues = (values: string[]): UserStatus[] =>
  USER_MANAGEMENT_STATUS_FILTER_OPTIONS.filter((status) =>
    values.includes(status),
  );

export const useUserManagementFilters = ({
  searchParams,
  setSearchParams,
}: UseUserManagementFiltersParams): UserManagementFiltersModel => {
  const { t } = useTranslation();

  const selectedStatuses = getValidStatusFilterValues(
    searchParams.getAll("status"),
  );

  const queryStatuses =
    selectedStatuses.length > 0
      ? selectedStatuses
      : [...USER_MANAGEMENT_STATUS_FILTER_OPTIONS];

  const statusOptions = useMemo(
    () =>
      USER_MANAGEMENT_STATUS_FILTER_OPTIONS.map((status) => ({
        value: status,
        label: t(($) => $.commons.user.statuses[status]),
      })),
    [t],
  );

  const setStatusFilter = (values: string[]) => {
    const statuses = getValidStatusFilterValues(values);
    const params = new URLSearchParams(searchParams);
    params.delete("status");

    statuses.forEach((status) => params.append("status", status));

    setSearchParams(params);
  };

  const clearStatusFilter = () => {
    const params = new URLSearchParams(searchParams);
    params.delete("status");
    setSearchParams(params);
  };

  return {
    selectedStatuses,
    queryStatuses,
    statusOptions,
    setStatusFilter,
    clearStatusFilter,
  };
};
